import { useReducedMotion } from 'motion/react';
import type { Transition, Variants } from 'motion/react';

/**
 * Shared motion vocabulary. Every animated component draws from these so the
 * site moves as one system: fade-rise reveals on scroll, stagger groups for
 * lists/grids, and spring presets for interactive elements. All effects are
 * transform/opacity only (GPU-composited — see the performance budget in the
 * build plan) and every consumer must gate continuous effects behind
 * useMotionEnabled().
 */

export const spring = {
  /** Snappy press/hover feedback on buttons and cards. */
  tap: { type: 'spring', stiffness: 400, damping: 25 } as Transition,
  /** Gentle settle for section reveals. */
  reveal: { type: 'spring', stiffness: 110, damping: 20, mass: 0.9 } as Transition,
};

/** Scroll-reveal: rise from below with a fade. Use with whileInView. */
export const fadeRise: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: spring.reveal },
};

/** Container that staggers its fadeRise children. */
export const staggerGroup: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
};

/**
 * Glow-line entrance, shared by every "line → spark star" lockup: the line
 * DRAWS outward from the text toward its star (set the transform-origin on the
 * text side) and the star pops last, completing the neon circuit. Used by the
 * hero's LIVE/POST heading and the Call-to-Rise WATCH NOW CTA (HX-48), so the
 * two read as the same fixture.
 *
 * GOTCHA: put `starPop` on a WRAPPER span, never on the star <img> itself — the
 * img carries a `rotate-90` Tailwind transform and Motion would overwrite the
 * whole inline transform while animating scale.
 */
export const lineDraw: Variants = {
  hidden: { scaleX: 0, opacity: 0 },
  visible: { scaleX: 1, opacity: 1, transition: spring.reveal },
};
export const starPop: Variants = {
  hidden: { opacity: 0, scale: 0.3 },
  visible: { opacity: 1, scale: 1, transition: { ...spring.reveal, delay: 0.22 } },
};

/** Standard whileInView props so sections reveal identically everywhere. */
export const revealViewport = { once: true, amount: 0.25 } as const;

/**
 * Single gate for all decorative/continuous motion (diamond travel, glow
 * breathing, particles). Respects prefers-reduced-motion; components fall
 * back to their static styled state (glows stay, travel stops).
 */
export function useMotionEnabled(): boolean {
  const reduced = useReducedMotion();
  return !reduced;
}
